import { DbBooking } from "../booking/models";
import { DbVisit } from "../visit/models";
import { Timestamp } from "firebase/firestore/lite";
import { CampaignFilter, CampaignFilterClient, ServiceRecord } from "./models";
import { DateTime } from "luxon";

// Calculate days since a specific timestamp
export const daysSince = (timestamp?: Timestamp): number => {
  if (!timestamp) return Infinity;

  const now = Timestamp.now();
  const diffMs = now.toMillis() - timestamp.toMillis();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const groupBookingsByUser = (bookings: DbBooking[]) => {
  return bookings.reduce(
    (acc, booking) => {
      if (!acc[booking.user_id]) {
        acc[booking.user_id] = [];
      }
      acc[booking.user_id].push(booking);
      return acc;
    },
    {} as Record<string, DbBooking[]>,
  );
};

export const hasRealValues = (obj: any): boolean => {
  if (!obj) return false;

  // For primitive values, check if they're not null/undefined
  if (typeof obj !== "object") return obj !== null && obj !== undefined;

  // For objects, check if any of their properties have real values
  return Object.values(obj).some((value) => hasRealValues(value));
};

export const groupVisitsByUser = (visits: DbVisit[]) => {
  return visits.reduce(
    (acc, visit) => {
      if (!acc[visit.user_id]) {
        acc[visit.user_id] = [];
      }
      acc[visit.user_id].push(visit);
      return acc;
    },
    {} as Record<string, DbVisit[]>,
  );
};

export const extractBookingIdsFromVisits = (visits: DbVisit[]): string[] => {
  const seen = new Set<string>();
  const ids: string[] = [];
  visits.forEach((v) => {
    (v.services || []).forEach((s) => {
      if (s.booking_id && !seen.has(s.booking_id)) {
        seen.add(s.booking_id);
        ids.push(s.booking_id);
      }
    });
  });
  return ids;
};

export const matchesFilter = (
  client: CampaignFilterClient,
  filter: CampaignFilter,
): boolean => {
  // Amount Spent
  if (filter.amount_spent && hasRealValues(filter.amount_spent)) {
    if (
      filter.amount_spent.more_than &&
      client.total_spent <= filter.amount_spent.more_than
    ) {
      return false;
    }

    if (
      filter.amount_spent.less_than &&
      client.total_spent >= filter.amount_spent.less_than
    ) {
      return false;
    }
  }
  // Client Presence
  if (filter.client_presence && hasRealValues(filter.client_presence)) {
    const { client_presence } = filter;
    const daysSinceCreation = daysSince(client.onboarded_at);
    const daysSinceLastService = client.last_service_date
      ? daysSince(client.last_service_date)
      : -1;
    // New Clients
    if (
      client_presence.new_clients &&
      ((filter.duration && daysSinceCreation > filter.duration.value) ||
        (filter.filter_start_date &&
          DateTime.fromMillis(filter.filter_start_date.toMillis()) >
            DateTime.fromMillis(client.onboarded_at.toMillis())))
    ) {
      return false;
    }
    // Not Booked any service in X days
    if (
      client_presence.not_booked_in_days &&
      daysSinceLastService !== -1 &&
      ((filter.duration && daysSinceLastService <= filter.duration.value) ||
        (filter.filter_start_date &&
          client.last_service_date &&
          DateTime.fromMillis(filter.filter_start_date.toMillis()) <
            DateTime.fromMillis(client.last_service_date?.toMillis())))
    ) {
      return false;
    }
    // Loyal Clients
    if (client_presence.loyal_clients?.min_bookings_per_year) {
      if (
        client.no_of_bookings_per_year <
        (client_presence.loyal_clients.min_bookings_per_year || 0)
      ) {
        return false;
      }
    }
    // Loyal but Lost Clients Check
    if (
      client_presence.loyal_but_lost_clients &&
      client_presence.loyal_but_lost_clients?.booking_frequency &&
      client_presence.loyal_but_lost_clients?.min_booking_duration &&
      client_presence.loyal_but_lost_clients?.min_booking_duration.unit &&
      client_presence.loyal_but_lost_clients?.min_booking_duration.value &&
      client_presence.loyal_but_lost_clients?.lost_duration &&
      client_presence.loyal_but_lost_clients?.lost_duration.unit &&
      client_presence.loyal_but_lost_clients?.lost_duration.value
    ) {
      const minBookings =
        client_presence.loyal_but_lost_clients?.booking_frequency;
      if (!minBookings) return false;
      const lostStartDate = DateTime.now().minus({
        [client_presence.loyal_but_lost_clients.lost_duration.unit]:
          client_presence.loyal_but_lost_clients.lost_duration.value,
      });
      if (
        client.no_of_bookings < minBookings ||
        (client.last_service_date &&
          DateTime.fromMillis(client.last_service_date.toMillis()) >
            lostStartDate)
      ) {
        return false;
      }
    }
  }

  // Service Usage
  if (filter.service_usage && hasRealValues(filter.service_usage)) {
    const { service_usage } = filter;
    // Used some service but not other
    if (service_usage.used_service_id && service_usage.not_used_service_id) {
      const usedServiceCount = client.services.filter(
        (service) => service === service_usage.used_service_id,
      ).length;

      const notUsedServiceCount = client.services.filter(
        (service) => service === service_usage.not_used_service_id,
      ).length;

      if (usedServiceCount === 0 || notUsedServiceCount > 0) return false;
    }
    // Used Service {{1}} time and didn't return
    if (
      service_usage.left_over_details &&
      service_usage.left_over_details.service_id &&
      service_usage.left_over_details.since_duration &&
      service_usage.left_over_details.booking_frequency
    ) {
      const { left_over_details } = service_usage;
      if (!left_over_details.service_id || !left_over_details.since_duration)
        return false;

      if (
        client.used_services.booking_frequency !==
        left_over_details.booking_frequency
      )
        return false;

      if (
        left_over_details.since_duration &&
        left_over_details.since_duration.unit
      ) {
        const sinceDuration = DateTime.now().minus({
          [left_over_details.since_duration?.unit]:
            left_over_details.since_duration.value,
        });

        const lastServiceDate = client.used_services.last_booked_at;
        if (lastServiceDate.toDate() < sinceDuration.toJSDate()) return false;
      }
    }
    // Never tried some service
    if (service_usage.not_tried_service_id) {
      // check if not_tried_service_id belongs to the client
      if (client.services.includes(service_usage.not_tried_service_id)) {
        return false;
      }
    }
  }
  // Gender
  if (filter.gender && filter.gender !== "all") {
    if (client.gender !== filter.gender) return false;
  }

  return true;
};
